import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import { buttonMessages } from "@saleor/intl";
import { DialogProps } from "@saleor/types";
import classNames from "classnames";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ConfirmButton, {
  ConfirmButtonTransitionState
} from "../ConfirmButton/ConfirmButton";

const useStyles = makeStyles(
  theme => ({
    cancelButton: {
      outline: `1px solid ${theme.palette.divider}`,
      padding: "6px 16px"
    },
    deleteButton: {
      "& span": {
        "& span": {
          color: "#002972"
        }
      },
      "&:hover": {
        backgroundColor: theme.palette.error.main
      },
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText
    },
    dialogButtons: (props: ActionDialogProps) => ({
      justifyContent: props.buttonsAlignment
    }),
    dialogContent: {
      padding: "24px !important"
    },
    fullWidthButtons: {
      display: "flex",
      ...{
        "& button": {
          flex: "1"
        }
      }
    },
    singleButton: {
      justifyContent: "center"
    }
  }),
  { name: "ActionDialog" }
);

export type ButtonKeys = keyof typeof buttonMessages;

interface ActionDialogProps extends DialogProps {
  actionBlocking?: boolean;
  buttonsAlignment?: "center" | "flex-end" | "flex-start";
  hideCancelButton?: boolean;
  children?: React.ReactNode;
  closeButtonLabel?: ButtonKeys | null;
  confirmButtonLabel?: string;
  confirmKeepLabel?: boolean;
  confirmButtonState: ConfirmButtonTransitionState;
  fullWidthButtons?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  title?: string;
  variant?: "default" | "delete" | "info";
  disabled?: boolean;
  onConfirm();
}

const ActionDialog: React.FC<ActionDialogProps> = props => {
  const {
    actionBlocking,
    buttonsAlignment,
    children,
    closeButtonLabel,
    confirmButtonLabel,
    confirmKeepLabel,
    confirmButtonState,
    fullWidthButtons,
    open,
    title,
    variant,
    disabled,
    onConfirm,
    onClose,
    hideCancelButton,
    ...rest
  } = props;

  const classes = useStyles(props);
  const intl = useIntl();

  return (
    <Dialog
      fullWidth
      onClose={
        confirmButtonState === "loading" && actionBlocking ? null : onClose
      }
      open={open}
      {...rest}
    >
      {title ? <DialogTitle>{title}</DialogTitle> : null}
      <DialogContent className={classes.dialogContent}>
        {children}
      </DialogContent>
      <DialogActions
        className={classNames(
          {
            [classes.fullWidthButtons]: fullWidthButtons,
            [classes.singleButton]: hideCancelButton
          },
          classes.dialogButtons
        )}
      >
        {hideCancelButton ? null : (
          <Button
            id={closeButtonLabel ? `${closeButtonLabel}Btn` : "cancelDialogBtn"}
            className={classes.cancelButton}
            disabled={actionBlocking && confirmButtonState === "loading"}
            onClick={onClose}
          >
            {closeButtonLabel ? (
              <FormattedMessage {...buttonMessages[closeButtonLabel]} />
            ) : (
              <FormattedMessage {...buttonMessages.cancel} />
            )}
          </Button>
        )}
        {variant !== "info" && (
          <ConfirmButton
            id={
              confirmButtonLabel
                ? `${confirmButtonLabel}Btn`
                : variant === "delete"
                ? "deleteDialogBtn"
                : "confirmDialogBtn"
            }
            transitionState={confirmButtonState}
            color="primary"
            disabled={disabled || confirmButtonState === "loading"}
            keepLabel={confirmKeepLabel}
            variant="contained"
            onClick={onConfirm}
            className={classNames({
              [classes.deleteButton]: variant === "delete"
            })}
          >
            {confirmButtonLabel ||
              (variant === "delete"
                ? intl.formatMessage(buttonMessages.delete)
                : intl.formatMessage(buttonMessages.confirm))}
          </ConfirmButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

ActionDialog.defaultProps = {
  actionBlocking: false,
  buttonsAlignment: "flex-end",
  closeButtonLabel: null,
  confirmKeepLabel: false,
  fullWidthButtons: false,
  maxWidth: "xs",
  variant: "default"
};
ActionDialog.displayName = "ActionDialog";
export default ActionDialog;
